import React, { useState, useEffect, useMemo, useRef } from "react";
import mammoth from "mammoth";
import {
  Printer,
  Download,
  Copy,
  Check,
  Sparkles,
  Sliders,
  UploadCloud,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  FileUp,
  FileCheck,
  Layers,
  ShieldCheck,
  ArrowLeft,
  ChevronRight,
} from "lucide-react";

/* ─────────────────────────────────────────────────────────────────────────
   SAMPLE TEMPLATE DATA (Extracted from ScienceXplorer Official PDF)
   ───────────────────────────────────────────────────────────────────────── */
export const SAMPLE_MANUSCRIPT = {
  articleType: "Review Article",
  title: "EFFECTS OF INCONCLUSIVE ELECTIONS ON DEMOCRATIC SUSTAINABILITY IN AFRICA",
  subtitle: "Investigating Nigeria's 2018–2023 Elections",
  volume: "8",
  issue: "2",
  year: "2026",
  doi: "10.62858/sx.2026.08.02",
  authors: "Author Name 1,*",
  affiliations: "1Department of Political Science, [Institution Name], Nigeria",
  correspondenceName: "Author Name",
  correspondenceEmail: "author@example.edu.ng",
  receivedDate: "—",
  acceptedDate: "—",
  publishedDate: "2026",
  citationAuthor: "Author, A.",
  licenseText: "© 2026 Author Name. Open-access under CC BY. Use, distribution or reproduction permitted provided the original author and source are credited.",
  abstract: `Inconclusive elections, contests suspended, rerun, or left legally and publicly contested well beyond polling day, have become one of the most visible symptoms of institutional strain in Nigeria's Fourth Republic. This paper investigates the incidence, causes, and consequences of inconclusive and disputed elections across the 2019 and 2023 general election cycles, using Nigeria as a bellwether case for the sustainability of electoral democracy in sub-Saharan Africa. Drawing on a documentary and comparative-descriptive review of the political science, electoral administration, and legal literature, together with electoral data reported by the Independent National Electoral Commission (INEC) and independent monitors, the paper traces four interacting drivers of inconclusiveness: electoral violence and its ethno-religious inflection, vote buying and clientelist mobilisation, technological and administrative failure in result collation and transmission, and structural weaknesses in judicial capacity to adjudicate disputes.

The analysis shows that the introduction of the Bimodal Voter Accreditation System (BVAS) and the INEC Result Viewing Portal (IReV) for the 2023 general election, though designed to resolve the credibility failures of the Smart Card Reader era, instead produced a new locus of dispute when result transmission failed at national scale, compounding a turnout decline that had already reached the lowest level in Nigeria's democratic history. The paper situates these findings within the wider debate on whether sub-Saharan Africa is undergoing systematic democratic backsliding, arguing that Nigeria's experience is better read as a case of persistent, unresolved institutional fragility than of straightforward autocratisation.

It concludes that democratic sustainability in Nigeria depends less on the introduction of new electoral technology than on binding statutory testing requirements for that technology, real independence for INEC, and a judiciary with the capacity and insulation to adjudicate election disputes quickly and credibly.`,
  keywords: "inconclusive elections, democratic consolidation, democratic backsliding, Nigeria, INEC, BVAS, electoral integrity, judiciary",
  body: `1. Introduction
Since the return to civilian rule on 29 May 1999, Nigeria's Fourth Republic has conducted six general election cycles without an interruption to constitutional government, a record that on its face suggests a degree of procedural consolidation unmatched by earlier attempts at democratic government in the country. Beneath that headline record, however, lies a persistent pattern of elections that end without a result that competitors and citizens broadly accept: rerun and supplementary polls at constituency level, court-ordered re-elections following successful petitions, and, in the most consequential cases, national results whose legitimacy remains contested in public discourse and in the courts long after the last vote has been counted.

The term "inconclusive election" entered Nigerian electoral vocabulary specifically to describe the practice, formalised by the Independent National Electoral Commission (INEC), of declaring a constituency-level contest inconclusive whenever the margin between the two leading candidates is narrower than the number of votes cancelled or otherwise unaccounted for, triggering a supplementary poll in the affected polling units.

This paper is concerned with the period bounded by the 2019 and 2023 general elections, a five-year window during which INEC undertook its most consequential technological reform to date: the introduction of the Bimodal Voter Accreditation System (BVAS) for voter authentication, paired with the INEC Result Viewing Portal (IReV) for real-time public disclosure of polling-unit results. Both reforms were introduced explicitly in response to the credibility failures of the preceding Smart Card Reader (SCR) regime, which had proved unable to reliably authenticate voter cards and biometric data (Akah et al., 2024).

2. Literature Review
2.1 Conceptualising Inconclusive Elections
Inconclusive or indeterminate election outcomes are not unique to Nigeria; deferred results occur periodically across representative democracies generally. What distinguishes the Nigerian pattern, according to the existing literature, is the frequency with which inconclusiveness appears to be tolerated, and at times engineered, by dominant political interests rather than arising purely from logistical accident. A review of inconclusive gubernatorial elections (IGEs) in Nigeria situates the phenomenon within a colonial and post-colonial inheritance in which political office is treated as a vehicle for personal enrichment rather than public service.

2.2 Democratic Consolidation, Backsliding, and the African Debate
Democratic sustainability, as used in this paper, is treated not as a binary achieved once at the point of transition, but as a continuing property of a political system: the capacity of its electoral institutions to produce outcomes that competitors and citizens accept as legitimate, cycle after cycle. This framing draws on the broader comparative literature on democratic backsliding (Arriola, Rakner, & van de Walle, 2023).

2.3 Electoral Violence and Democratic Consolidation in Nigeria
A substantial Nigerian literature treats electoral violence as both a symptom and a cause of weak democratic consolidation. Ashindorbe (2018) examines the factors that make Nigerian elections prone to violence, situating the problem in the fragility of a plural, ethnically and religiously divided polity.

2.4 Vote Buying, Clientelism, and Voter Turnout
A parallel literature examines vote buying as both a cause of democratic erosion and, counter-intuitively, a weak predictor of the electoral outcomes it is intended to secure. Bratton's (2008) Afrobarometer-based analysis found that vote buying was substantially more prevalent in rural than urban areas.

2.5 Judicial Capacity and the Adjudication of Disputed Elections
Because Nigerian law routes virtually every seriously contested election through a tribunal or the courts, the judiciary functions as the de facto final stage of the electoral process, and its capacity and independence bear directly on whether an inconclusive or disputed result is ultimately resolved.

2.6 Electoral Technology: BVAS and IReV
The most recent strand of the literature examines Nigeria's 2023 investment in biometric and digital transmission technology. INEC's stated rationale for introducing BVAS was to remedy the specific technical limitations of the Smart Card Reader system.

3. Methodology
This paper adopts a documentary and comparative-descriptive research design, consistent with the predominant methodological approach in the Nigerian inconclusive-elections literature reviewed above. Data are drawn from three categories of source: (i) peer-reviewed political science, legal, and electoral-administration literature; (ii) official electoral data reported by INEC and election observers; and (iii) comparative cross-national democratisation data.

4. Findings I: The 2019 Baseline
The 2019 general election was conducted using the Smart Card Reader (SCR) system introduced for the 2015 election. Comparative turnout analysis finds a consistent decline that authors attribute to voter disillusionment and weak institutional credibility.

Table 1. Voter Turnout in Nigerian General (Presidential) Elections, 1999–2023
| Election Year | Turnout (%) | Approx. Registered (M) | Notable Feature |
| 1999 | 52.3 | ~57.9 | First election of Fourth Republic |
| 2003 | 69.0 | ~60.8 | Peak post-transition turnout |
| 2007 | 57.5 | ~61.6 | Widely criticised administration |
| 2011 | 53.7 | ~73.5 | First biometric voter registration |
| 2015 | 43.7 | ~68.8 | First opposition defeat of incumbent |
| 2019 | 34.8 | ~82.3 | Smart Card Reader-administered |
| 2023 | 28.6 | ~93.4 | BVAS/IReV; lowest turnout in history |
Source: Compiled from Premium Times (2023) and The Conversation (2023/2025) INEC data.

5. Findings II: The 2023 Reform and Its Paradox
INEC's introduction of BVAS for the 2023 general election was framed as a direct response to the accreditation failures of the SCR era. In practice, result transmission failed at national scale, compounding a turnout decline that had already reached the lowest level in Nigeria's democratic history.

Figure 1. Conceptual Pathway from Institutional Weakness to Democratic Erosion
Weak institutional independence & unfunded judiciary -> Election-day administrative/technological failure -> Inconclusive/disputed result -> Eroded voter & institutional trust -> Decreased democratic sustainability.

6. Findings III: Judicial Adjudication as the Residual Arbiter
Because Nigerian electoral law routes contested results through election petition tribunals, the judiciary's perceived independence determines whether an election is resolved in a manner that restores confidence or compounds its loss.

7. Findings IV: Vote Buying, Clientelism, and Turnout Collapse
The turnout collapse documented is best understood not as a single-cause phenomenon but as the cumulative product of violence, technological failure, and judicial fragility, compounded by clientelist mobilisation.

8. Discussion
Three findings emerge from this review: first, technological reform without independent enforcement authority does not resolve credibility deficits; second, Nigeria's experience illustrates persistent institutional fragility rather than simple autocratisation; third, repeated inconclusiveness degrades trust across both the electoral commission and the judiciary.

9. Conclusion and Policy Implications
Democratic sustainability in Nigeria is not secured by the introduction of new electoral technology alone. Institutional insulation, binding statutory testing requirements, and judicial independence must accompany technological investment if inconclusive elections are to stop functioning as a recurring threat.`,
  references: `Afripoli. (2023). Will there be meaningful democratic reforms in Nigeria post the 2023 elections? https://afripoli.org/will-there-be-meaningful-democratic-reforms-in-nigeria-post-the-2023-elections
Akah, A. U., Edino, O. F., Agbor, U. I., Nwagboso, C. I., Musa, A., Adams, J. A., Ebegbulem, J., Eja, T. R., Bassey, U. S., Ogar, J. I., Nwagboso, S. N., & Ekpo, S. O. (2024). Elections administration and Bimodal Voter Accreditation System (BVAS) technology: Interrogating the 2023 Nigerian presidential election. International Journal of Public Administration in the Digital Age, 11(1), 1–30. https://doi.org/10.4018/IJPADA.352042
Arriola, L. R., Rakner, L., & van de Walle, N. (Eds.). (2023). Democratic backsliding in Africa? Autocratization, resilience, and contention. Oxford University Press. https://doi.org/10.1093/oso/9780192867322.003.0001
Ashindorbe, K. (2018). Electoral violence and the challenge of democratic consolidation in Nigeria. India Quarterly, 74(1), 92–105. https://doi.org/10.1177/0974928417749639
Bratton, M. (2008). Vote buying and violence in Nigerian election campaigns. Afrobarometer Working Paper No. 99.
Chiamogu, U. P. (2026). Electoral participation and democratic consolidation in Nigeria: A comparative analysis of the 2015, 2019 and 2023 presidential elections. African Journal of Political Science, 13(2), 82–100. https://doi.org/10.36615/h6ksec53
International IDEA. (2022). Inspiring confidence in the BVAS and electronic transmission of election results: Seven urgent actions for INEC.
Kuenzi, M., & Lambright, G. M. S. (2011). Who votes in Africa? An examination of electoral participation in 10 African countries. https://doi.org/10.1177/1354068810376779
Lambe, E. O., et al. (2023). Political violence and democratic consolidation in Nigeria: A focus on the 2019 and 2023 general elections. African Journal of Politics and Administrative Studies.
Udemezue, S. (2026). Election petition adjudication and judicial capacity in Nigeria. SSRN.`
};

/* ─────────────────────────────────────────────────────────────────────────
   AUTOMATED WORD (.DOCX) DOCUMENT PARSER
   Extracts Title, Subtitle, Authors, Affiliations, Abstract, Keywords,
   Two-Column Sections, Academic Tables, and References from Word documents
   ───────────────────────────────────────────────────────────────────────── */
export function convertDocxHtmlToLines(html, rawText) {
  let text = html || "";

  // 1. Convert Word tables to Markdown pipe table format
  let tableCounter = 1;
  text = text.replace(/<table[\s\S]*?<\/table>/gi, (match) => {
    const trMatches = match.match(/<tr[\s\S]*?<\/tr>/gi) || [];
    const rows = [];
    for (const tr of trMatches) {
      const cellMatches = tr.match(/<(td|th)[\s\S]*?<\/(td|th)>/gi) || [];
      const cells = cellMatches.map((c) =>
        c.replace(/<[^>]+>/g, "").replace(/\|/g, "-").trim()
      );
      if (cells.length > 0) {
        rows.push("| " + cells.join(" | ") + " |");
      }
    }
    if (rows.length > 0) {
      return `\n\nTable ${tableCounter++}.\n` + rows.join("\n") + "\n\n";
    }
    return "";
  });

  // 2. Headings
  text = text.replace(/<h1[^>]*>([\s\S]*?)<\/h1>/gi, (_, h) => "\n\n# " + h.replace(/<[^>]+>/g, "").trim() + "\n\n");
  text = text.replace(/<h[2-6][^>]*>([\s\S]*?)<\/h[2-6]>/gi, (_, h) => "\n\n## " + h.replace(/<[^>]+>/g, "").trim() + "\n\n");

  // 3. Keep superscripts in place for author affiliations
  text = text.replace(/<sup>([\s\S]*?)<\/sup>/gi, (_, s) => "<sup>" + s.trim() + "</sup>");

  // 4. Paragraphs and lists
  text = text.replace(/<p[^>]*>([\s\S]*?)<\/p>/gi, (_, p) => "\n\n" + p.trim() + "\n\n");
  text = text.replace(/<li[^>]*>([\s\S]*?)<\/li>/gi, (_, l) => "\n- " + l.trim() + "\n");
  text = text.replace(/<br\s*\/?>/gi, "\n");

  // 5. Strip other tags except <sup>
  text = text.replace(/<(?!sup|\/sup)[^>]+>/g, " ");

  // 6. Decode entities
  text = text
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");

  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);

  if (!lines.length && rawText) {
    return rawText.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  }

  return lines;
}

export function parseStructuredDocxLines(lines, rawText, fileName = "") {
  const result = {
    title: "",
    subtitle: "",
    authors: "",
    affiliations: "",
    correspondenceName: "",
    correspondenceEmail: "",
    articleType: "Original Research",
    abstract: "",
    keywords: "",
    body: "",
    references: "",
    receivedDate: "12 January 2026",
    acceptedDate: "18 February 2026",
    publishedDate: "2026",
    volume: "8",
    issue: "2",
    year: "2026",
    doi: "10.62858/sx.2026.08.02",
  };

  // Find emails in document
  const emails = rawText.match(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g) || [];
  if (emails.length > 0) {
    result.correspondenceEmail = emails[0];
  }

  let mode = "header"; // header | abstract | keywords | body | references
  const headerLines = [];
  const abstractLines = [];
  const bodyLines = [];
  const refLines = [];

  for (let i = 0; i < lines.length; i++) {
    const rawLine = lines[i];
    const cleanLine = rawLine.replace(/^[#]+\s*/, "").trim();

    // Check for References heading
    if (/^(references|bibliography|works cited|literature cited)\b/i.test(cleanLine)) {
      mode = "references";
      continue;
    }

    // Check for Keywords
    if (/^keywords?\s*[:-]/i.test(cleanLine) || /^index\s+terms\s*[:-]/i.test(cleanLine)) {
      result.keywords = cleanLine.replace(/^(keywords?|index\s+terms)\s*[:-]\s*/i, "").trim();
      mode = "body";
      continue;
    }

    // Check for Abstract
    if (/^abstract\b/i.test(cleanLine)) {
      mode = "abstract";
      const rest = cleanLine.replace(/^abstract\s*[:-]?\s*/i, "").trim();
      if (rest) abstractLines.push(rest);
      continue;
    }

    // Check for first numbered section e.g. 1. Introduction
    if (/^[0-9]+\.\s*(introduction|background|overview)/i.test(cleanLine)) {
      mode = "body";
      bodyLines.push(cleanLine);
      continue;
    }

    if (mode === "references") {
      refLines.push(cleanLine);
    } else if (mode === "body") {
      bodyLines.push(cleanLine);
    } else if (mode === "abstract") {
      // If we encounter a section while reading abstract
      if (/^[0-9]+\.\s*[A-Z]/.test(cleanLine)) {
        mode = "body";
        bodyLines.push(cleanLine);
      } else {
        abstractLines.push(cleanLine);
      }
    } else if (mode === "header") {
      headerLines.push(cleanLine);
    }
  }

  // Parse header items
  for (let i = 0; i < headerLines.length; i++) {
    const line = headerLines[i];

    if (/^title\s*[:-]\s*/i.test(line)) {
      result.title = line.replace(/^title\s*[:-]\s*/i, "").trim();
      continue;
    }
    if (/^subtitle\s*[:-]\s*/i.test(line)) {
      result.subtitle = line.replace(/^subtitle\s*[:-]\s*/i, "").trim();
      continue;
    }
    if (/^article\s*type\s*[:-]\s*/i.test(line)) {
      result.articleType = line.replace(/^article\s*type\s*[:-]\s*/i, "").trim();
      continue;
    }
    if (/^(review article|original research|case study|short communication|policy brief)$/i.test(line)) {
      result.articleType = line;
      continue;
    }
    if (/^authors?\s*[:-]\s*/i.test(line)) {
      result.authors = line.replace(/^authors?\s*[:-]\s*/i, "").trim();
      continue;
    }
    if (/^affiliation(s)?\s*[:-]\s*/i.test(line)) {
      const aff = line.replace(/^affiliation(s)?\s*[:-]\s*/i, "").trim();
      result.affiliations = result.affiliations ? result.affiliations + "; " + aff : aff;
      continue;
    }
    if (/^correspondence\s*[:-]\s*/i.test(line) || /^corresponding\s+author\s*[:-]\s*/i.test(line)) {
      const rest = line.replace(/^(correspondence|corresponding\s+author)\s*[:-]\s*/i, "").trim();
      if (rest.includes("@")) {
        const em = rest.match(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/);
        if (em) result.correspondenceEmail = em[0];
      }
      result.correspondenceName = rest
        .replace(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/, "")
        .replace(/[*:]/g, "")
        .trim();
      continue;
    }

    // Heuristics without explicit prefix:
    if (!result.title) {
      result.title = line.toUpperCase();
      continue;
    }

    if (
      !result.subtitle &&
      line.length < 90 &&
      (line.startsWith("A Study") ||
        line.startsWith("Investigating") ||
        line.startsWith("An Empirical") ||
        line.startsWith("Towards") ||
        line.startsWith("Assessing") ||
        line.startsWith("The Role"))
    ) {
      result.subtitle = line;
      continue;
    }

    if (
      !result.authors &&
      !/^[0-9]/.test(line) &&
      !/department|university|faculty|institute|college|school|hospital|centre|center/i.test(line)
    ) {
      result.authors = line;
      continue;
    }

    if (/^[0-9]/.test(line) || /department|university|faculty|institute|college|school|hospital|centre|center/i.test(line)) {
      result.affiliations = result.affiliations ? result.affiliations + "; " + line : line;
      continue;
    }

    if (line.length > 100) {
      abstractLines.push(line);
    }
  }

  // Fallbacks
  if (!result.title && fileName) {
    result.title = fileName.replace(/\.[^/.]+$/, "").replace(/[_-]/g, " ").toUpperCase();
  }
  if (!result.authors) {
    result.authors = "Author Name 1,*";
  }
  if (!result.correspondenceName && result.authors) {
    result.correspondenceName = result.authors.split(/[,;1-9*]/)[0].trim() || "Author Name";
  }

  result.abstract = abstractLines.join("\n\n").trim();
  result.body = bodyLines.join("\n\n").trim();
  result.references = refLines.join("\n").trim();

  if (!result.body && lines.length > 5) {
    result.body = lines.slice(Math.min(5, lines.length - 1)).join("\n\n");
  }

  const wordCount = rawText ? rawText.trim().split(/\s+/).length : 0;
  const sectionsCount = (result.body.match(/^[0-9]+\.\s+[A-Z]/gm) || []).length;
  const referencesCount = result.references ? result.references.split(/\r?\n/).filter(Boolean).length : 0;
  const tablesCount = (result.body.match(/Table\s+[0-9]+/gi) || []).length;

  return {
    ...result,
    stats: {
      fileName,
      wordCount,
      sectionsCount: Math.max(sectionsCount, 1),
      referencesCount,
      tablesCount,
    },
  };
}

export async function parseWordDocx(arrayBuffer, fileName = "manuscript.docx") {
  const [rawResult, htmlResult] = await Promise.all([
    mammoth.extractRawText({ arrayBuffer }),
    mammoth.convertToHtml({ arrayBuffer }),
  ]);

  const rawText = rawResult.value || "";
  const html = htmlResult.value || "";

  const lines = convertDocxHtmlToLines(html, rawText);
  return parseStructuredDocxLines(lines, rawText, fileName);
}

/* ─────────────────────────────────────────────────────────────────────────
   SMART RAW-TEXT PARSER
   Extracts Title, Subtitle, Authors, Abstract, Keywords, Sections, References
   ───────────────────────────────────────────────────────────────────────── */
export function parseRawManuscript(rawText) {
  if (!rawText || !rawText.trim()) return null;

  const lines = rawText.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const result = {
    title: "",
    subtitle: "",
    authors: "",
    affiliations: "",
    correspondenceEmail: "",
    abstract: "",
    keywords: "",
    body: "",
    references: "",
    articleType: "Review Article",
  };

  let mode = "header"; // header | abstract | keywords | body | references
  const bodyLines = [];
  const refLines = [];
  const abstractLines = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Detect section boundaries
    if (/^(references|bibliography|works cited)/i.test(line)) {
      mode = "references";
      continue;
    }
    if (/^keywords?\s*[:-]/i.test(line)) {
      result.keywords = line.replace(/^keywords?\s*[:-]\s*/i, "").trim();
      mode = "body";
      continue;
    }
    if (/^abstract\s*[:-]?$/i.test(line) || /^abstract\s*[:-]/i.test(line)) {
      mode = "abstract";
      const rest = line.replace(/^abstract\s*[:-]?\s*/i, "");
      if (rest) abstractLines.push(rest);
      continue;
    }
    if (/^(1\.\s*introduction|introduction)/i.test(line)) {
      mode = "body";
      bodyLines.push(line);
      continue;
    }

    if (mode === "references") {
      refLines.push(line);
    } else if (mode === "body") {
      bodyLines.push(line);
    } else if (mode === "abstract") {
      abstractLines.push(line);
    } else if (mode === "header") {
      // Check for explicit tags
      if (/^title\s*[:-]\s*/i.test(line)) {
        result.title = line.replace(/^title\s*[:-]\s*/i, "").trim();
      } else if (/^subtitle\s*[:-]\s*/i.test(line)) {
        result.subtitle = line.replace(/^subtitle\s*[:-]\s*/i, "").trim();
      } else if (/^authors?\s*[:-]\s*/i.test(line)) {
        result.authors = line.replace(/^authors?\s*[:-]\s*/i, "").trim();
      } else if (/^affiliation(s)?\s*[:-]\s*/i.test(line)) {
        result.affiliations = line.replace(/^affiliation(s)?\s*[:-]\s*/i, "").trim();
      } else if (/^correspondence\s*[:-]\s*/i.test(line)) {
        result.correspondenceEmail = line.replace(/^correspondence\s*[:-]\s*/i, "").trim();
      } else if (!result.title) {
        result.title = line;
      } else if (!result.subtitle && (line.length < 80 || line.startsWith("Investigating") || line.startsWith("A "))) {
        result.subtitle = line;
      } else if (!result.authors) {
        result.authors = line;
      } else if (!result.affiliations) {
        result.affiliations = line;
      } else {
        // Fallthrough to abstract if it looks like prose
        abstractLines.push(line);
        mode = "abstract";
      }
    }
  }

  result.abstract = abstractLines.join("\n\n").trim();
  result.body = bodyLines.join("\n\n").trim();
  result.references = refLines.join("\n").trim();

  // If no body was collected, try to extract from rawText
  if (!result.body && abstractLines.length > 3) {
    result.body = abstractLines.slice(2).join("\n\n");
    result.abstract = abstractLines.slice(0, 2).join("\n\n");
  }

  return result;
}

/* ─────────────────────────────────────────────────────────────────────────
   MAIN MANUSCRIPT FORMATTER COMPONENT
   ───────────────────────────────────────────────────────────────────────── */
export default function ManuscriptFormatter({ initialData = null, onBack = null }) {
  const [data, setData] = useState(() => {
    // Check localStorage draft first, then initialData, then sample
    try {
      const draft = localStorage.getItem("sx_formatter_draft");
      if (draft) {
        const parsed = JSON.parse(draft);
        return { ...SAMPLE_MANUSCRIPT, ...parsed };
      }
    } catch (e) {}
    if (initialData) {
      return { ...SAMPLE_MANUSCRIPT, ...initialData };
    }
    return SAMPLE_MANUSCRIPT;
  });

  const [activeTab, setActiveTab] = useState("upload"); // "upload" | "form" | "paste"
  const [rawInput, setRawInput] = useState("");
  const [viewMode, setViewMode] = useState("a4"); // "a4" | "continuous"
  const [zoom, setZoom] = useState(100);
  const [copied, setCopied] = useState(false);
  const [appliedNotification, setAppliedNotification] = useState("");
  const previewRef = useRef(null);
  const headerFileInputRef = useRef(null);
  const dropzoneFileInputRef = useRef(null);

  // Word Document Upload State
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStep, setUploadStep] = useState(""); // "reading" | "extracting" | "formatting" | ""
  const [uploadReport, setUploadReport] = useState(null);
  const [uploadError, setUploadError] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [availableManuscripts, setAvailableManuscripts] = useState([]);

  useEffect(() => {
    try {
      const stored = localStorage.getItem("sx_cache_manuscripts:all") || localStorage.getItem("sx_manuscripts");
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setAvailableManuscripts(parsed);
        }
      }
    } catch (e) {}
  }, []);

  // Handle Word (.docx) File Upload & Auto-Format
  const handleWordFileUpload = async (file) => {
    if (!file) return;
    const name = file.name || "";
    const isDocx = name.toLowerCase().endsWith(".docx") || name.toLowerCase().endsWith(".doc");
    if (!isDocx) {
      setUploadError("Please select a valid Microsoft Word document (.docx or .doc).");
      return;
    }

    setUploadError("");
    setIsUploading(true);
    setUploadStep("reading");

    try {
      const arrayBuffer = await file.arrayBuffer();

      setUploadStep("extracting");
      await new Promise((r) => setTimeout(r, 350));

      const parsed = await parseWordDocx(arrayBuffer, file.name);

      setUploadStep("formatting");
      await new Promise((r) => setTimeout(r, 350));

      setData((prev) => ({
        ...prev,
        title: parsed.title || prev.title,
        subtitle: parsed.subtitle || prev.subtitle,
        authors: parsed.authors || prev.authors,
        affiliations: parsed.affiliations || prev.affiliations,
        correspondenceName: parsed.correspondenceName || prev.correspondenceName,
        correspondenceEmail: parsed.correspondenceEmail || prev.correspondenceEmail,
        articleType: parsed.articleType || prev.articleType,
        abstract: parsed.abstract || prev.abstract,
        keywords: parsed.keywords || prev.keywords,
        body: parsed.body || prev.body,
        references: parsed.references || prev.references,
        receivedDate: parsed.receivedDate || prev.receivedDate,
        acceptedDate: parsed.acceptedDate || prev.acceptedDate,
        publishedDate: parsed.publishedDate || prev.publishedDate,
        volume: parsed.volume || prev.volume,
        issue: parsed.issue || prev.issue,
        year: parsed.year || prev.year,
        doi: parsed.doi || prev.doi,
      }));

      const fileSizeKb = Math.max(1, Math.round(file.size / 1024));
      setUploadReport({
        fileName: file.name,
        fileSizeKb,
        title: parsed.title,
        wordCount: parsed.stats.wordCount,
        sectionsCount: parsed.stats.sectionsCount,
        referencesCount: parsed.stats.referencesCount,
        tablesCount: parsed.stats.tablesCount,
      });

      setAppliedNotification(`Word document "${file.name}" formatted into template!`);
      setTimeout(() => setAppliedNotification(""), 4500);
    } catch (err) {
      console.error("Word parse error:", err);
      setUploadError(
        `Failed to parse Word document (${err.message || "Unknown error"}). You can also copy and paste the text into the "Quick Paste" tab.`
      );
    } finally {
      setIsUploading(false);
      setUploadStep("");
    }
  };

  // Load from submission queue
  const handleLoadFromQueue = (m) => {
    if (!m) return;
    setData((prev) => ({
      ...prev,
      title: m.title ? m.title.toUpperCase() : prev.title,
      authors: m.authors || prev.authors,
      affiliations: m.affiliations || prev.affiliations,
      correspondenceEmail: m.author_email || prev.author_email,
      abstract: m.abstract || prev.abstract,
      keywords: m.keywords || prev.keywords,
      articleType: m.category || "Original Research",
    }));
    setAppliedNotification(`Loaded "${(m.title || "manuscript").slice(0, 35)}..." into template!`);
    setTimeout(() => setAppliedNotification(""), 3500);
  };

  // Auto-save to localStorage
  useEffect(() => {
    try {
      localStorage.setItem("sx_formatter_draft", JSON.stringify(data));
    } catch (e) {}
  }, [data]);

  // Derived citation
  const formattedCitation = useMemo(() => {
    const author = data.citationAuthor || data.authors?.split(",")?.[0]?.trim() || "Author, A.";
    const year = data.publishedDate || data.year || "2026";
    const vol = data.volume || "8";
    const iss = data.issue || "2";
    const titleText = data.subtitle ? `${data.title}: ${data.subtitle}` : data.title;
    return `${author} (${year}). ${titleText}. ScienceXplorer, ${vol}(${iss}).`;
  }, [data]);

  // Handle Apply Raw Text
  const handleParseAndApply = () => {
    if (!rawInput.trim()) return;
    const parsed = parseRawManuscript(rawInput);
    if (parsed) {
      setData((prev) => ({
        ...prev,
        title: parsed.title || prev.title,
        subtitle: parsed.subtitle || prev.subtitle,
        authors: parsed.authors || prev.authors,
        affiliations: parsed.affiliations || prev.affiliations,
        correspondenceEmail: parsed.correspondenceEmail || prev.correspondenceEmail,
        abstract: parsed.abstract || prev.abstract,
        keywords: parsed.keywords || prev.keywords,
        body: parsed.body || prev.body,
        references: parsed.references || prev.references,
      }));
      setAppliedNotification("Successfully extracted and applied into the ScienceXplorer template!");
      setTimeout(() => setAppliedNotification(""), 3500);
    }
  };

  // Load sample template
  const handleLoadSample = () => {
    setData(SAMPLE_MANUSCRIPT);
    setAppliedNotification("Official ScienceXplorer template loaded!");
    setTimeout(() => setAppliedNotification(""), 3000);
  };

  // Print / Save as PDF
  const handlePrint = () => {
    window.print();
  };

  // Copy citation
  const handleCopyCitation = () => {
    navigator.clipboard.writeText(formattedCitation);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Download standalone HTML
  const handleDownloadHtml = () => {
    const previewEl = previewRef.current;
    if (!previewEl) return;
    const htmlContent = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <title>${data.title} — ScienceXplorer</title>
  <style>
    @page { size: A4; margin: 12mm 15mm; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; margin: 0; padding: 20px; color: #111827; background: #fff; }
    .sx-template-wrapper { max-width: 860px; margin: 0 auto; }
    ${printStyleSheet}
  </style>
</head>
<body>
  <div class="sx-template-wrapper">
    ${previewEl.innerHTML}
  </div>
</body>
</html>`;
    const blob = new Blob([htmlContent], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ScienceXplorer_${(data.title || "manuscript").slice(0, 30).replace(/[^a-zA-Z0-9]/g, "_")}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Parse body into structured sections & blocks
  const parsedSections = useMemo(() => {
    if (!data.body) return [];
    const paragraphs = data.body.split(/\r?\n\r?\n/);
    const sections = [];
    let currentSection = { heading: "", items: [] };

    paragraphs.forEach((p) => {
      const trimmed = p.trim();
      if (!trimmed) return;

      // Table detection
      if (trimmed.startsWith("Table ") || (trimmed.includes("|") && trimmed.includes("\n|"))) {
        currentSection.items.push({ type: "table", content: trimmed });
        return;
      }
      // Figure detection
      if (trimmed.startsWith("Figure ")) {
        currentSection.items.push({ type: "figure", content: trimmed });
        return;
      }
      // Major heading (e.g. 1. Introduction, 2. Literature Review)
      if (/^[0-9]+\.\s+[A-Z]/.test(trimmed) && trimmed.length < 80) {
        if (currentSection.heading || currentSection.items.length) {
          sections.push(currentSection);
        }
        currentSection = { heading: trimmed, items: [] };
        return;
      }
      // Subheading (e.g. 2.1 Conceptualising...)
      if (/^[0-9]+\.[0-9]+\s+[A-Z]/.test(trimmed) && trimmed.length < 100) {
        currentSection.items.push({ type: "subheading", content: trimmed });
        return;
      }
      // Standard prose paragraph
      currentSection.items.push({ type: "p", content: trimmed });
    });

    if (currentSection.heading || currentSection.items.length) {
      sections.push(currentSection);
    }
    return sections;
  }, [data.body]);

  // Parse references
  const parsedReferences = useMemo(() => {
    if (!data.references) return [];
    return data.references
      .split(/\r?\n/)
      .map((r) => r.trim())
      .filter(Boolean);
  }, [data.references]);

  return (
    <div className="sx-formatter-container" style={{ background: "#F3F4F6", minHeight: "100vh" }}>
      {/* ── TOP HEADER / TOOLBAR ── */}
      <header
        className="no-print"
        style={{
          background: "#111827",
          color: "white",
          padding: "12px 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "16px",
          boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
          position: "sticky",
          top: 0,
          zIndex: 90,
        }}
      >
        <input
          type="file"
          ref={headerFileInputRef}
          accept=".docx,.doc"
          onChange={(e) => {
            if (e.target.files?.[0]) {
              handleWordFileUpload(e.target.files[0]);
              e.target.value = "";
            }
          }}
          style={{ display: "none" }}
        />

        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {onBack && (
            <button
              onClick={onBack}
              style={{
                background: "rgba(255,255,255,0.12)",
                border: "1px solid rgba(255,255,255,0.2)",
                color: "white",
                padding: "6px 12px",
                fontFamily: "var(--mono)",
                fontSize: "11px",
                cursor: "pointer",
                borderRadius: "4px",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <ArrowLeft size={13} /> Back to Admin Portal
            </button>
          )}
          <div style={{ display: "flex", alignItems: "baseline", gap: "2px" }}>
            <span style={{ fontFamily: "var(--serif-display)", fontSize: "18px", fontWeight: 900, color: "white" }}>
              Science
            </span>
            <span style={{ fontFamily: "var(--serif-display)", fontSize: "18px", fontWeight: 900, color: "#EF4444", fontStyle: "italic" }}>
              X
            </span>
            <span style={{ fontFamily: "var(--serif-display)", fontSize: "18px", fontWeight: 900, color: "white" }}>
              plorer
            </span>
          </div>
          <span
            style={{
              background: "#991B1B",
              color: "#FEE2E2",
              fontFamily: "var(--mono)",
              fontSize: "10px",
              fontWeight: 700,
              padding: "2px 8px",
              borderRadius: "12px",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <ShieldCheck size={11} /> Admin Only
          </span>
        </div>

        {/* Action Controls */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
          <button
            onClick={() => headerFileInputRef.current?.click()}
            disabled={isUploading}
            style={{
              background: "#DC2626",
              color: "white",
              border: "none",
              padding: "7px 14px",
              fontFamily: "var(--mono)",
              fontSize: "11px",
              fontWeight: 700,
              cursor: isUploading ? "wait" : "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              borderRadius: "4px",
              boxShadow: "0 2px 6px rgba(220, 38, 38, 0.4)",
            }}
            title="Upload any Word document (.docx) to automatically parse into the journal template"
          >
            <FileUp size={13} />
            {isUploading ? "Auto-Parsing Word..." : "Upload Word (.docx)"}
          </button>

          <button
            onClick={handleLoadSample}
            style={{
              background: "rgba(255,255,255,0.12)",
              color: "white",
              border: "1px solid rgba(255,255,255,0.2)",
              padding: "7px 12px",
              fontFamily: "var(--mono)",
              fontSize: "11px",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              borderRadius: "4px",
            }}
            title="Load the official ScienceXplorer sample template"
          >
            <Sparkles size={13} color="#FBBF24" /> Load Official Sample
          </button>

          <button
            onClick={handleCopyCitation}
            style={{
              background: "rgba(255,255,255,0.12)",
              color: "white",
              border: "1px solid rgba(255,255,255,0.2)",
              padding: "7px 12px",
              fontFamily: "var(--mono)",
              fontSize: "11px",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              borderRadius: "4px",
            }}
          >
            {copied ? <Check size={13} color="#10B981" /> : <Copy size={13} />}
            {copied ? "Citation Copied!" : "Copy Citation"}
          </button>

          <button
            onClick={handleDownloadHtml}
            style={{
              background: "rgba(255,255,255,0.12)",
              color: "white",
              border: "1px solid rgba(255,255,255,0.2)",
              padding: "7px 12px",
              fontFamily: "var(--mono)",
              fontSize: "11px",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              borderRadius: "4px",
            }}
          >
            <Download size={13} /> Export HTML
          </button>

          <button
            onClick={handlePrint}
            style={{
              background: "#991B1B",
              color: "white",
              border: "none",
              padding: "8px 16px",
              fontFamily: "var(--mono)",
              fontSize: "11px",
              fontWeight: 700,
              letterSpacing: "0.05em",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "7px",
              borderRadius: "4px",
              boxShadow: "0 2px 8px rgba(153, 27, 27, 0.4)",
            }}
          >
            <Printer size={14} /> Print / Save as PDF
          </button>
        </div>
      </header>

      {/* Notification Toast */}
      {appliedNotification && (
        <div
          className="no-print animate-fade"
          style={{
            position: "fixed",
            bottom: "24px",
            right: "24px",
            zIndex: 1000,
            background: "#065F46",
            color: "#ECFDF5",
            padding: "12px 20px",
            borderRadius: "6px",
            fontFamily: "var(--mono)",
            fontSize: "12px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
          }}
        >
          <CheckCircle2 size={16} /> {appliedNotification}
        </div>
      )}

      {/* ── WORKSPACE: TWO PANE (EDITOR ON LEFT, LIVE PREVIEW ON RIGHT) ── */}
      <div
        className="no-print-layout"
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(340px, 460px) 1fr",
          minHeight: "calc(100vh - 56px)",
          background: "#F3F4F6",
        }}
      >
        {/* ── LEFT PANE: INPUTS & CONTROLS ── */}
        <aside
          className="no-print"
          style={{
            background: "white",
            borderRight: "1px solid #E5E7EB",
            height: "calc(100vh - 56px)",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Tabs */}
          <div
            style={{
              display: "flex",
              borderBottom: "1px solid #E5E7EB",
              background: "#F9FAFB",
            }}
          >
            <button
              onClick={() => setActiveTab("upload")}
              style={{
                flex: 1,
                padding: "12px 6px",
                fontFamily: "var(--mono)",
                fontSize: "11px",
                fontWeight: activeTab === "upload" ? 700 : 500,
                color: activeTab === "upload" ? "#991B1B" : "#6B7280",
                background: activeTab === "upload" ? "white" : "transparent",
                border: "none",
                borderBottom: activeTab === "upload" ? "2px solid #991B1B" : "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "5px",
              }}
            >
              <FileUp size={13} /> Word (.docx)
            </button>
            <button
              onClick={() => setActiveTab("form")}
              style={{
                flex: 1,
                padding: "12px 6px",
                fontFamily: "var(--mono)",
                fontSize: "11px",
                fontWeight: activeTab === "form" ? 700 : 500,
                color: activeTab === "form" ? "#991B1B" : "#6B7280",
                background: activeTab === "form" ? "white" : "transparent",
                border: "none",
                borderBottom: activeTab === "form" ? "2px solid #991B1B" : "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "5px",
              }}
            >
              <Sliders size={13} /> Structured Fields
            </button>
            <button
              onClick={() => setActiveTab("paste")}
              style={{
                flex: 1,
                padding: "12px 6px",
                fontFamily: "var(--mono)",
                fontSize: "11px",
                fontWeight: activeTab === "paste" ? 700 : 500,
                color: activeTab === "paste" ? "#991B1B" : "#6B7280",
                background: activeTab === "paste" ? "white" : "transparent",
                border: "none",
                borderBottom: activeTab === "paste" ? "2px solid #991B1B" : "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "5px",
              }}
            >
              <UploadCloud size={13} /> Quick Paste
            </button>
          </div>

          <div style={{ padding: "20px", flex: 1 }}>
            {activeTab === "upload" ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                {/* Description Banner */}
                <div
                  style={{
                    background: "#FEF2F2",
                    border: "1px solid #FECACA",
                    borderRadius: "6px",
                    padding: "12px 14px",
                    fontFamily: "var(--mono)",
                    fontSize: "11px",
                    color: "#991B1B",
                    lineHeight: 1.5,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "6px", fontWeight: 700, marginBottom: "4px" }}>
                    <Sparkles size={14} /> Automated Word (.docx) Typesetting
                  </div>
                  <div>
                    Upload an author's Word document. ScienceXplorer automatically extracts the Title, Authors, Affiliations, Abstract, Keywords, 2-Column Sections, Academic Tables, and References directly into the publication template.
                  </div>
                </div>

                {/* Hidden dropzone file input */}
                <input
                  type="file"
                  ref={dropzoneFileInputRef}
                  accept=".docx,.doc"
                  onChange={(e) => {
                    if (e.target.files?.[0]) {
                      handleWordFileUpload(e.target.files[0]);
                      e.target.value = "";
                    }
                  }}
                  style={{ display: "none" }}
                />

                {/* Drag & Drop Area */}
                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDragging(true);
                  }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setIsDragging(false);
                    if (e.dataTransfer.files?.[0]) {
                      handleWordFileUpload(e.dataTransfer.files[0]);
                    }
                  }}
                  onClick={() => {
                    if (!isUploading) {
                      dropzoneFileInputRef.current?.click();
                    }
                  }}
                  style={{
                    border: isDragging ? "2px dashed #991B1B" : "2px dashed #CBD5E1",
                    background: isDragging ? "#FEF2F2" : isUploading ? "#F9FAFB" : "#FFFFFF",
                    borderRadius: "8px",
                    padding: "26px 16px",
                    textAlign: "center",
                    cursor: isUploading ? "wait" : "pointer",
                    transition: "all 0.15s ease-in-out",
                    boxShadow: isDragging ? "0 4px 14px rgba(153, 27, 27, 0.15)" : "none",
                  }}
                >
                  <div
                    style={{
                      width: "46px",
                      height: "46px",
                      margin: "0 auto 10px",
                      borderRadius: "50%",
                      background: isUploading ? "#FEF2F2" : "#F3F4F6",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: isUploading ? "#991B1B" : "#4B5563",
                    }}
                  >
                    {isUploading ? (
                      <RefreshCw size={22} className="animate-spin" />
                    ) : (
                      <FileUp size={22} />
                    )}
                  </div>

                  <div style={{ fontFamily: "var(--serif-display)", fontSize: "15px", fontWeight: 700, color: "#111827", marginBottom: "4px" }}>
                    {isUploading ? "Auto-Parsing Word Document..." : "Drop Word Document (.docx) Here"}
                  </div>

                  <div style={{ fontFamily: "var(--mono)", fontSize: "11px", color: "#6B7280", marginBottom: "12px" }}>
                    {isUploading
                      ? uploadStep === "reading"
                        ? "1/3 Reading Word OpenXML stream..."
                        : uploadStep === "extracting"
                        ? "2/3 Extracting titles, authors, abstract & sections..."
                        : "3/3 Typesetting 2-column layout & bibliography..."
                      : "or click anywhere to browse (.docx or .doc)"}
                  </div>

                  {!isUploading && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        dropzoneFileInputRef.current?.click();
                      }}
                      style={{
                        background: "#991B1B",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        padding: "8px 16px",
                        fontFamily: "var(--mono)",
                        fontSize: "11px",
                        fontWeight: 700,
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <UploadCloud size={13} /> Choose .docx File
                    </button>
                  )}

                  {/* Progress Indicator */}
                  {isUploading && (
                    <div style={{ marginTop: "12px", display: "flex", flexDirection: "column", gap: "6px" }}>
                      <div style={{ width: "100%", height: "4px", background: "#E5E7EB", borderRadius: "2px", overflow: "hidden" }}>
                        <div
                          style={{
                            height: "100%",
                            background: "#991B1B",
                            width: uploadStep === "reading" ? "35%" : uploadStep === "extracting" ? "70%" : "95%",
                            transition: "width 0.3s ease",
                          }}
                        />
                      </div>
                      <span style={{ fontFamily: "var(--mono)", fontSize: "10px", color: "#991B1B", fontWeight: 600 }}>
                        {uploadStep === "reading" && "Extracting binary Word archive..."}
                        {uploadStep === "extracting" && "Parsing document structure & sections..."}
                        {uploadStep === "formatting" && "Injecting into ScienceXplorer template..."}
                      </span>
                    </div>
                  )}
                </div>

                {/* Upload Error */}
                {uploadError && (
                  <div
                    style={{
                      background: "#FFF1F2",
                      border: "1px solid #FECDD3",
                      color: "#9F1239",
                      padding: "10px 12px",
                      borderRadius: "6px",
                      fontFamily: "var(--mono)",
                      fontSize: "11px",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "8px",
                    }}
                  >
                    <AlertCircle size={15} style={{ flexShrink: 0, marginTop: "2px" }} />
                    <div>{uploadError}</div>
                  </div>
                )}

                {/* Extraction Report Card */}
                {uploadReport && (
                  <div
                    style={{
                      background: "#F0FDF4",
                      border: "1px solid #BBF7D0",
                      borderRadius: "8px",
                      padding: "14px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px", color: "#166534", fontFamily: "var(--mono)", fontSize: "11px", fontWeight: 700 }}>
                        <FileCheck size={14} /> Auto-Formatted into Template
                      </div>
                      <span style={{ fontFamily: "var(--mono)", fontSize: "10px", color: "#15803D", background: "#DCFCE7", padding: "2px 6px", borderRadius: "10px" }}>
                        {uploadReport.fileSizeKb} KB
                      </span>
                    </div>

                    <div style={{ fontFamily: "var(--serif-display)", fontSize: "13px", fontWeight: 700, color: "#111827", lineHeight: 1.3 }}>
                      {uploadReport.title || "Extracted Manuscript"}
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "8px",
                        background: "white",
                        padding: "10px",
                        borderRadius: "6px",
                        border: "1px solid #DCFCE7",
                      }}
                    >
                      <div>
                        <div style={{ fontFamily: "var(--mono)", fontSize: "9px", color: "#6B7280", textTransform: "uppercase" }}>Word Count</div>
                        <div style={{ fontFamily: "var(--mono)", fontSize: "13px", fontWeight: 700, color: "#111827" }}>
                          {uploadReport.wordCount.toLocaleString()}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontFamily: "var(--mono)", fontSize: "9px", color: "#6B7280", textTransform: "uppercase" }}>Sections</div>
                        <div style={{ fontFamily: "var(--mono)", fontSize: "13px", fontWeight: 700, color: "#111827" }}>
                          {uploadReport.sectionsCount}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontFamily: "var(--mono)", fontSize: "9px", color: "#6B7280", textTransform: "uppercase" }}>Tables</div>
                        <div style={{ fontFamily: "var(--mono)", fontSize: "13px", fontWeight: 700, color: "#111827" }}>
                          {uploadReport.tablesCount}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontFamily: "var(--mono)", fontSize: "9px", color: "#6B7280", textTransform: "uppercase" }}>References</div>
                        <div style={{ fontFamily: "var(--mono)", fontSize: "13px", fontWeight: 700, color: "#111827" }}>
                          {uploadReport.referencesCount}
                        </div>
                      </div>
                    </div>

                    <div style={{ display: "flex", gap: "8px", marginTop: "4px" }}>
                      <button
                        onClick={() => setActiveTab("form")}
                        style={{
                          flex: 1,
                          padding: "8px 10px",
                          background: "#166534",
                          color: "white",
                          border: "none",
                          borderRadius: "4px",
                          fontFamily: "var(--mono)",
                          fontSize: "10px",
                          fontWeight: 700,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "4px",
                        }}
                      >
                        Review in Structured Editor <ChevronRight size={12} />
                      </button>
                      <button
                        onClick={() => dropzoneFileInputRef.current?.click()}
                        style={{
                          padding: "8px 10px",
                          background: "white",
                          color: "#374151",
                          border: "1px solid #D1D5DB",
                          borderRadius: "4px",
                          fontFamily: "var(--mono)",
                          fontSize: "10px",
                          cursor: "pointer",
                        }}
                      >
                        Upload Another
                      </button>
                    </div>
                  </div>
                )}

                {/* Submissions Queue Quick-Loader */}
                {availableManuscripts.length > 0 && (
                  <div
                    style={{
                      border: "1px solid #E5E7EB",
                      borderRadius: "8px",
                      padding: "12px",
                      background: "#F9FAFB",
                    }}
                  >
                    <div
                      style={{
                        fontFamily: "var(--mono)",
                        fontSize: "10px",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        color: "#4B5563",
                        marginBottom: "8px",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <Layers size={13} /> Or Load from Submissions Queue ({availableManuscripts.length})
                    </div>
                    <select
                      onChange={(e) => {
                        const found = availableManuscripts.find((m) => String(m.id) === e.target.value);
                        if (found) handleLoadFromQueue(found);
                        e.target.value = "";
                      }}
                      defaultValue=""
                      style={{
                        width: "100%",
                        padding: "8px 10px",
                        fontFamily: "var(--mono)",
                        fontSize: "11px",
                        border: "1px solid #D1D5DB",
                        borderRadius: "4px",
                        background: "white",
                        outline: "none",
                      }}
                    >
                      <option value="" disabled>
                        Select submitted manuscript to load...
                      </option>
                      {availableManuscripts.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.title ? `${m.title.slice(0, 40)}... (${m.authors || "Author"})` : `Manuscript #${m.id}`}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            ) : activeTab === "paste" ? (
              <div>
                <div
                  style={{
                    background: "#FEF2F2",
                    border: "1px solid #FECACA",
                    borderRadius: "6px",
                    padding: "12px 14px",
                    marginBottom: "16px",
                    fontFamily: "var(--mono)",
                    fontSize: "11px",
                    color: "#991B1B",
                    lineHeight: 1.5,
                  }}
                >
                  <strong>⚡ Instant Manuscript Formatter:</strong> Paste your entire raw paper text (Word/PDF extract/draft) below. Our parser will detect the Title, Subtitle, Authors, Abstract, Keywords, Sections, and References, and place them directly into the ScienceXplorer template.
                </div>
                <label
                  style={{
                    display: "block",
                    fontFamily: "var(--mono)",
                    fontSize: "10px",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    color: "#374151",
                    marginBottom: "6px",
                  }}
                >
                  Raw Manuscript Text
                </label>
                <textarea
                  rows={18}
                  placeholder={`Paste text here, for example:\n\nTITLE: EFFECTS OF CLIMATE ON...\nSUBTITLE: A study of...\nAUTHORS: John Doe 1, Jane Smith 2\nAFFILIATIONS: 1University of X, 2Institute of Y\nCORRESPONDENCE: john@x.edu\n\nABSTRACT\nIn this study...\n\nKEYWORDS: climate, resilience, ecology\n\n1. Introduction\nSince recent decades...\n\nReferences\nSmith, J. (2025)...`}
                  value={rawInput}
                  onChange={(e) => setRawInput(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "12px",
                    fontFamily: "var(--mono)",
                    fontSize: "11px",
                    border: "1px solid #D1D5DB",
                    borderRadius: "4px",
                    boxSizing: "border-box",
                    outline: "none",
                    lineHeight: 1.5,
                    resize: "vertical",
                  }}
                />
                <div style={{ display: "flex", gap: "10px", marginTop: "12px" }}>
                  <button
                    onClick={handleParseAndApply}
                    disabled={!rawInput.trim()}
                    style={{
                      flex: 1,
                      padding: "12px",
                      background: rawInput.trim() ? "#991B1B" : "#E5E7EB",
                      color: rawInput.trim() ? "white" : "#9CA3AF",
                      border: "none",
                      borderRadius: "4px",
                      fontFamily: "var(--mono)",
                      fontSize: "11px",
                      fontWeight: 700,
                      cursor: rawInput.trim() ? "pointer" : "not-allowed",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "6px",
                    }}
                  >
                    <Sparkles size={14} /> Format & Put in Template
                  </button>
                  <button
                    onClick={() => setRawInput("")}
                    style={{
                      padding: "12px 14px",
                      background: "none",
                      border: "1px solid #D1D5DB",
                      borderRadius: "4px",
                      color: "#6B7280",
                      cursor: "pointer",
                      fontFamily: "var(--mono)",
                      fontSize: "11px",
                    }}
                  >
                    Clear
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                {/* Article Type & Volume/Issue */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                  <div>
                    <label className="field-label">Article Type</label>
                    <select
                      className="field"
                      style={{ padding: "8px 10px", fontSize: "12px", borderRadius: "3px" }}
                      value={data.articleType}
                      onChange={(e) => setData({ ...data, articleType: e.target.value })}
                    >
                      <option value="Review Article">Review Article</option>
                      <option value="Original Research">Original Research</option>
                      <option value="Case Study">Case Study</option>
                      <option value="Methodology">Methodology</option>
                      <option value="Brief Report">Brief Report</option>
                      <option value="Opinion / Commentary">Opinion / Commentary</option>
                    </select>
                  </div>
                  <div>
                    <label className="field-label">DOI Identifier</label>
                    <input
                      className="field"
                      style={{ padding: "8px 10px", fontSize: "12px", borderRadius: "3px" }}
                      value={data.doi}
                      placeholder="e.g. 10.62858/sx.2026.08.02"
                      onChange={(e) => setData({ ...data, doi: e.target.value })}
                    />
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px" }}>
                  <div>
                    <label className="field-label">Volume</label>
                    <input
                      className="field"
                      style={{ padding: "8px 10px", fontSize: "12px", borderRadius: "3px" }}
                      value={data.volume}
                      onChange={(e) => setData({ ...data, volume: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="field-label">Issue</label>
                    <input
                      className="field"
                      style={{ padding: "8px 10px", fontSize: "12px", borderRadius: "3px" }}
                      value={data.issue}
                      onChange={(e) => setData({ ...data, issue: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="field-label">Year</label>
                    <input
                      className="field"
                      style={{ padding: "8px 10px", fontSize: "12px", borderRadius: "3px" }}
                      value={data.year}
                      onChange={(e) => setData({ ...data, year: e.target.value })}
                    />
                  </div>
                </div>

                {/* Title */}
                <div>
                  <label className="field-label">Article Title (All Caps)</label>
                  <textarea
                    rows={2}
                    className="field"
                    style={{ padding: "8px 10px", fontSize: "13px", fontWeight: 700, borderRadius: "3px", textTransform: "uppercase" }}
                    value={data.title}
                    onChange={(e) => setData({ ...data, title: e.target.value })}
                  />
                </div>

                {/* Subtitle */}
                <div>
                  <label className="field-label">Subtitle (Optional)</label>
                  <input
                    className="field"
                    style={{ padding: "8px 10px", fontSize: "12px", fontStyle: "italic", borderRadius: "3px" }}
                    value={data.subtitle}
                    onChange={(e) => setData({ ...data, subtitle: e.target.value })}
                  />
                </div>

                {/* Authors */}
                <div>
                  <label className="field-label">Authors (with superscripts e.g. 1,*)</label>
                  <input
                    className="field"
                    style={{ padding: "8px 10px", fontSize: "12px", borderRadius: "3px" }}
                    value={data.authors}
                    onChange={(e) => setData({ ...data, authors: e.target.value })}
                  />
                </div>

                {/* Affiliations */}
                <div>
                  <label className="field-label">Affiliations</label>
                  <textarea
                    rows={2}
                    className="field"
                    style={{ padding: "8px 10px", fontSize: "12px", borderRadius: "3px" }}
                    value={data.affiliations}
                    onChange={(e) => setData({ ...data, affiliations: e.target.value })}
                  />
                </div>

                {/* Corresponding Email */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                  <div>
                    <label className="field-label">Correspondence Name</label>
                    <input
                      className="field"
                      style={{ padding: "8px 10px", fontSize: "12px", borderRadius: "3px" }}
                      value={data.correspondenceName}
                      onChange={(e) => setData({ ...data, correspondenceName: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="field-label">Correspondence Email</label>
                    <input
                      className="field"
                      style={{ padding: "8px 10px", fontSize: "12px", borderRadius: "3px" }}
                      value={data.correspondenceEmail}
                      onChange={(e) => setData({ ...data, correspondenceEmail: e.target.value })}
                    />
                  </div>
                </div>

                {/* Abstract */}
                <div>
                  <label className="field-label">Abstract</label>
                  <textarea
                    rows={6}
                    className="field"
                    style={{ padding: "10px", fontSize: "12px", lineHeight: 1.5, borderRadius: "3px" }}
                    value={data.abstract}
                    onChange={(e) => setData({ ...data, abstract: e.target.value })}
                  />
                </div>

                {/* Keywords */}
                <div>
                  <label className="field-label">Keywords (comma-separated)</label>
                  <input
                    className="field"
                    style={{ padding: "8px 10px", fontSize: "12px", borderRadius: "3px" }}
                    value={data.keywords}
                    onChange={(e) => setData({ ...data, keywords: e.target.value })}
                  />
                </div>

                {/* Body Sections */}
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                    <label className="field-label">Main Body (Sections & Paragraphs)</label>
                    <span style={{ fontFamily: "var(--mono)", fontSize: "9px", color: "#6B7280" }}>Two-Column Formatted</span>
                  </div>
                  <textarea
                    rows={12}
                    className="field"
                    style={{ padding: "10px", fontSize: "12px", lineHeight: 1.5, borderRadius: "3px" }}
                    value={data.body}
                    placeholder={`1. Introduction\nText here...\n\n2. Literature Review\nText here...\n\n2.1 Conceptualising...\nText here...`}
                    onChange={(e) => setData({ ...data, body: e.target.value })}
                  />
                </div>

                {/* References */}
                <div>
                  <label className="field-label">References (one per line)</label>
                  <textarea
                    rows={6}
                    className="field"
                    style={{ padding: "10px", fontSize: "11px", lineHeight: 1.4, borderRadius: "3px" }}
                    value={data.references}
                    placeholder={`Author, A. (2025). Article title. ScienceXplorer, 1(1). https://doi.org/...`}
                    onChange={(e) => setData({ ...data, references: e.target.value })}
                  />
                </div>
              </div>
            )}
          </div>
        </aside>

        {/* ── RIGHT PANE: INTERACTIVE LIVE PREVIEW ── */}
        <main
          style={{
            padding: "24px 20px",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {/* View Toolbar */}
          <div
            className="no-print"
            style={{
              width: "100%",
              maxWidth: "840px",
              background: "white",
              padding: "10px 18px",
              borderRadius: "8px",
              boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
              marginBottom: "20px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "10px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ fontFamily: "var(--mono)", fontSize: "11px", color: "#6B7280", textTransform: "uppercase" }}>
                Layout View:
              </span>
              <button
                onClick={() => setViewMode("a4")}
                style={{
                  background: viewMode === "a4" ? "#991B1B" : "#F3F4F6",
                  color: viewMode === "a4" ? "white" : "#374151",
                  border: "none",
                  padding: "5px 10px",
                  borderRadius: "4px",
                  fontFamily: "var(--mono)",
                  fontSize: "11px",
                  cursor: "pointer",
                }}
              >
                A4 Paginated View
              </button>
              <button
                onClick={() => setViewMode("continuous")}
                style={{
                  background: viewMode === "continuous" ? "#991B1B" : "#F3F4F6",
                  color: viewMode === "continuous" ? "white" : "#374151",
                  border: "none",
                  padding: "5px 10px",
                  borderRadius: "4px",
                  fontFamily: "var(--mono)",
                  fontSize: "11px",
                  cursor: "pointer",
                }}
              >
                Continuous Scroll
              </button>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ fontFamily: "var(--mono)", fontSize: "11px", color: "#6B7280" }}>Scale:</span>
              {[90, 100, 110].map((z) => (
                <button
                  key={z}
                  onClick={() => setZoom(z)}
                  style={{
                    background: zoom === z ? "#E5E7EB" : "transparent",
                    border: "1px solid #D1D5DB",
                    color: "#374151",
                    padding: "3px 8px",
                    borderRadius: "3px",
                    fontFamily: "var(--mono)",
                    fontSize: "10px",
                    cursor: "pointer",
                  }}
                >
                  {z}%
                </button>
              ))}
            </div>
          </div>

          {/* ── THE EXACT SCIENCE XPLORER TEMPLATE RENDERER ── */}
          <div
            ref={previewRef}
            className={`sx-template-root ${viewMode === "a4" ? "sx-view-a4" : "sx-view-continuous"}`}
            style={{
              transform: `scale(${zoom / 100})`,
              transformOrigin: "top center",
              transition: "transform 0.15s ease",
            }}
          >
            {/* ═════════════════════════════════════════════════════════════
               PAGE 1: COVER / TITLE / ABSTRACT / HOW TO CITE
               ═════════════════════════════════════════════════════════════ */}
            <div className="sx-page sx-page-1" id="page-1">
              <div className="sx-page-content" style={{ display: "flex", flexDirection: "column", height: "100%", justifyContent: "space-between" }}>
                <div>
                  <div style={{ display: "grid", gridTemplateColumns: "185px 1px 1fr", gap: "0", minHeight: "820px" }}>
                    {/* LEFT COLUMN: METADATA & SIDEBAR */}
                    <div style={{ paddingRight: "18px" }}>
                      {/* Logo */}
                      <div style={{ marginBottom: "26px" }}>
                        <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: "20px", fontWeight: 900, color: "#991B1B", letterSpacing: "-0.01em" }}>
                          ScienceXplorer
                        </div>
                      </div>

                      {/* Article Type */}
                      <div style={{ marginBottom: "14px" }}>
                        <div className="sx-side-label">TYPE</div>
                        <div className="sx-side-val" style={{ fontWeight: 600 }}>{data.articleType || "Review Article"}</div>
                      </div>

                      {/* Published Year */}
                      <div style={{ marginBottom: "14px" }}>
                        <div className="sx-side-label">PUBLISHED</div>
                        <div className="sx-side-val">{data.publishedDate || data.year || "2026"}</div>
                      </div>

                      {/* DOI */}
                      <div style={{ marginBottom: "16px" }}>
                        <div className="sx-side-label">DOI</div>
                        <div className="sx-side-val" style={{ color: "#991B1B", wordBreak: "break-all" }}>
                          {data.doi ? data.doi.replace(/^https?:\/\/(?:dx\.)?doi\.org\//i, "") : `10.62858/sx.${data.year || 2026}.${data.volume || 8}.${data.issue || 2}`}
                        </div>
                      </div>

                      {/* Open Access Badge */}
                      <div style={{ marginBottom: "20px" }}>
                        <div style={{ fontFamily: "Arial, sans-serif", fontSize: "11px", fontWeight: 800, color: "#111827", letterSpacing: "0.06em", textTransform: "uppercase" }}>
                          OPEN ACCESS
                        </div>
                      </div>

                      {/* Correspondence */}
                      <div style={{ marginBottom: "16px" }}>
                        <div className="sx-side-label">*CORRESPONDENCE</div>
                        <div className="sx-side-val" style={{ fontWeight: 600 }}>{data.correspondenceName || data.authors?.split(",")?.[0]?.replace(/[0-9,*]/g, "").trim() || "Author Name"}</div>
                        <div className="sx-side-val" style={{ color: "#991B1B", wordBreak: "break-all" }}>{data.correspondenceEmail || "author@example.edu.ng"}</div>
                      </div>

                      {/* Dates */}
                      <div style={{ marginBottom: "14px" }}>
                        <div className="sx-side-label">RECEIVED</div>
                        <div className="sx-side-val">{data.receivedDate || "—"}</div>
                      </div>
                      <div style={{ marginBottom: "14px" }}>
                        <div className="sx-side-label">ACCEPTED</div>
                        <div className="sx-side-val">{data.acceptedDate || "—"}</div>
                      </div>
                      <div style={{ marginBottom: "20px" }}>
                        <div className="sx-side-label">PUBLISHED</div>
                        <div className="sx-side-val">{data.publishedDate || data.year || "2026"}</div>
                      </div>

                      {/* Citation in sidebar */}
                      <div style={{ marginBottom: "20px" }}>
                        <div className="sx-side-label">CITATION</div>
                        <div style={{ fontFamily: "Arial, sans-serif", fontSize: "9.5px", lineHeight: 1.45, color: "#374151" }}>
                          {formattedCitation}
                        </div>
                      </div>

                      {/* Copyright */}
                      <div>
                        <div className="sx-side-label">COPYRIGHT</div>
                        <div style={{ fontFamily: "Arial, sans-serif", fontSize: "9px", lineHeight: 1.45, color: "#4B5563" }}>
                          {data.licenseText || `© ${data.year || 2026} Author Name. Open-access under CC BY. Use, distribution or reproduction permitted provided the original author and source are credited.`}
                        </div>
                      </div>
                    </div>

                    {/* VERTICAL DIVIDER LINE */}
                    <div style={{ background: "#991B1B", opacity: 0.35, width: "1px" }} />

                    {/* RIGHT COLUMN: TITLE, AUTHORS, ABSTRACT, KEYWORDS */}
                    <div style={{ paddingLeft: "24px" }}>
                      {/* Title */}
                      <h1
                        style={{
                          fontFamily: "Arial, Helvetica, sans-serif",
                          fontSize: "20px",
                          fontWeight: 800,
                          lineHeight: 1.25,
                          color: "#991B1B",
                          textTransform: "uppercase",
                          margin: "0 0 8px 0",
                          letterSpacing: "-0.01em",
                        }}
                      >
                        {data.title || "TITLE OF MANUSCRIPT"}
                      </h1>

                      {/* Subtitle */}
                      {data.subtitle && (
                        <h2
                          style={{
                            fontFamily: "Georgia, serif",
                            fontSize: "13.5px",
                            fontStyle: "italic",
                            color: "#991B1B",
                            fontWeight: 400,
                            margin: "0 0 14px 0",
                            lineHeight: 1.35,
                          }}
                        >
                          {data.subtitle}
                        </h2>
                      )}

                      {/* Authors */}
                      <div
                        style={{
                          fontFamily: "Arial, Helvetica, sans-serif",
                          fontSize: "12px",
                          fontWeight: 700,
                          color: "#111827",
                          marginBottom: "4px",
                        }}
                      >
                        {data.authors || "Author Name 1,*"}
                      </div>

                      {/* Affiliations */}
                      <div
                        style={{
                          fontFamily: "Arial, Helvetica, sans-serif",
                          fontSize: "10.5px",
                          color: "#374151",
                          lineHeight: 1.4,
                          marginBottom: "4px",
                        }}
                      >
                        {data.affiliations || "1Department of Discipline, [Institution Name], Country"}
                      </div>

                      {/* Corresponding author footnote */}
                      <div
                        style={{
                          fontFamily: "Arial, Helvetica, sans-serif",
                          fontSize: "10px",
                          fontStyle: "italic",
                          color: "#4B5563",
                          marginBottom: "20px",
                        }}
                      >
                        *Corresponding author
                      </div>

                      {/* ABSTRACT HEADER */}
                      <div
                        style={{
                          fontFamily: "Arial, Helvetica, sans-serif",
                          fontSize: "12px",
                          fontWeight: 800,
                          textTransform: "uppercase",
                          letterSpacing: "0.08em",
                          color: "#111827",
                          marginBottom: "10px",
                        }}
                      >
                        ABSTRACT
                      </div>

                      {/* ABSTRACT TEXT */}
                      <div
                        style={{
                          fontFamily: "Arial, Helvetica, sans-serif",
                          fontSize: "10.5px",
                          lineHeight: 1.6,
                          color: "#1F2937",
                          textAlign: "justify",
                          marginBottom: "18px",
                          whiteSpace: "pre-line",
                        }}
                      >
                        {data.abstract || "Abstract text goes here..."}
                      </div>

                      {/* KEYWORDS */}
                      <div
                        style={{
                          fontFamily: "Arial, Helvetica, sans-serif",
                          fontSize: "10px",
                          lineHeight: 1.5,
                          color: "#111827",
                        }}
                      >
                        <strong>Keywords:</strong>{" "}
                        <span style={{ fontStyle: "italic" }}>
                          {data.keywords || "keyword one, keyword two, keyword three"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* HOW TO CITE BOX (BOTTOM OF PAGE 1) */}
                  <div
                    style={{
                      marginTop: "24px",
                      border: "1px solid #991B1B",
                      padding: "12px 16px",
                      background: "transparent",
                    }}
                  >
                    <div
                      style={{
                        fontFamily: "Arial, Helvetica, sans-serif",
                        fontSize: "11px",
                        fontWeight: 700,
                        color: "#991B1B",
                        marginBottom: "4px",
                      }}
                    >
                      How to Cite
                    </div>
                    <div
                      style={{
                        fontFamily: "Arial, Helvetica, sans-serif",
                        fontSize: "10px",
                        lineHeight: 1.45,
                        color: "#1F2937",
                      }}
                    >
                      {formattedCitation}
                    </div>
                  </div>
                </div>

                {/* FOOTER PAGE 1 */}
                <div
                  style={{
                    borderTop: "1px solid transparent",
                    paddingTop: "14px",
                    textAlign: "center",
                    fontFamily: "Arial, Helvetica, sans-serif",
                    fontSize: "10px",
                    color: "#6B7280",
                  }}
                >
                  1 &nbsp;|&nbsp; Volume {data.volume || 8}, Issue {data.issue || 2}
                </div>
              </div>
            </div>

            {/* ═════════════════════════════════════════════════════════════
               PAGE 2+: TWO-COLUMN BODY SECTIONS, TABLES & REFERENCES
               ═════════════════════════════════════════════════════════════ */}
            <div className="sx-page sx-page-body" id="page-2">
              {/* Running Header */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  borderBottom: "1px solid transparent",
                  paddingBottom: "8px",
                  marginBottom: "16px",
                }}
              >
                <span
                  style={{
                    fontFamily: "Arial, Helvetica, sans-serif",
                    fontSize: "10.5px",
                    fontStyle: "italic",
                    color: "#991B1B",
                  }}
                >
                  ScienceXplorer.org
                </span>
              </div>

              {/* TWO COLUMN ARTICLE CONTENT */}
              <div className="sx-two-columns">
                {parsedSections.map((sec, sIdx) => (
                  <div key={sIdx} style={{ marginBottom: "14px", breakInside: "avoid-column" }}>
                    {sec.heading && (
                      <h3 className="sx-heading-1">
                        {sec.heading}
                      </h3>
                    )}
                    {sec.items.map((item, iIdx) => {
                      if (item.type === "subheading") {
                        return (
                          <h4 key={iIdx} className="sx-heading-2">
                            {item.content}
                          </h4>
                        );
                      }
                      if (item.type === "table") {
                        return <FormattedTable key={iIdx} raw={item.content} />;
                      }
                      if (item.type === "figure") {
                        return <FormattedFigure key={iIdx} raw={item.content} />;
                      }
                      return (
                        <p key={iIdx} className="sx-body-p">
                          {item.content}
                        </p>
                      );
                    })}
                  </div>
                ))}

                {/* REFERENCES HEADING & LIST */}
                {parsedReferences.length > 0 && (
                  <div style={{ marginTop: "24px", breakInside: "avoid-column" }}>
                    <h3 className="sx-heading-1" style={{ color: "#991B1B" }}>
                      References
                    </h3>
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginTop: "10px" }}>
                      {parsedReferences.map((ref, rIdx) => (
                        <div key={rIdx} className="sx-reference-item">
                          {formatReferenceText(ref)}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Running Footer on Subsequent Pages */}
              <div
                style={{
                  marginTop: "30px",
                  paddingTop: "14px",
                  textAlign: "center",
                  fontFamily: "Arial, Helvetica, sans-serif",
                  fontSize: "10px",
                  color: "#6B7280",
                }}
              >
                Volume {data.volume || 8}, Issue {data.issue || 2} &nbsp;|&nbsp; ScienceXplorer
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* ── CSS STYLES SPECIFIC TO THE SCIENCE XPLORER TEMPLATE ── */}
      <style>{`
        /* SIDEBAR LABELS */
        .sx-side-label {
          font-family: Arial, Helvetica, sans-serif;
          font-size: 9px;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #111827;
          margin-bottom: 2px;
        }
        .sx-side-val {
          font-family: Arial, Helvetica, sans-serif;
          font-size: 10px;
          color: #374151;
          line-height: 1.35;
        }

        /* HEADINGS */
        .sx-heading-1 {
          font-family: Arial, Helvetica, sans-serif;
          font-size: 12.5px;
          font-weight: 700;
          color: #991B1B;
          margin: 16px 0 8px 0;
          line-height: 1.35;
          letter-spacing: -0.01em;
        }
        .sx-heading-2 {
          font-family: Arial, Helvetica, sans-serif;
          font-size: 11.5px;
          font-weight: 700;
          color: #991B1B;
          margin: 12px 0 6px 0;
          line-height: 1.35;
        }

        /* BODY PARAGRAPHS */
        .sx-body-p {
          font-family: Arial, Helvetica, sans-serif;
          font-size: 10.5px;
          line-height: 1.55;
          color: #1F2937;
          text-align: justify;
          margin-bottom: 10px;
          text-justify: inter-word;
        }

        /* TWO COLUMN CONTAINER */
        .sx-two-columns {
          column-count: 2;
          column-gap: 28px;
          text-align: justify;
        }

        /* REFERENCES HANGING INDENT */
        .sx-reference-item {
          font-family: Arial, Helvetica, sans-serif;
          font-size: 9.5px;
          line-height: 1.45;
          color: #1F2937;
          padding-left: 20px;
          text-indent: -20px;
          word-break: break-word;
        }
        .sx-ref-link {
          color: #991B1B;
          text-decoration: underline;
          word-break: break-all;
        }

        /* VIEW CONTAINER MODES */
        .sx-view-a4 .sx-page {
          width: 820px;
          min-height: 1160px;
          background: white;
          padding: 44px 50px;
          margin-bottom: 30px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.08);
          box-sizing: border-box;
          position: relative;
        }

        .sx-view-continuous .sx-page {
          width: 100%;
          max-width: 860px;
          background: white;
          padding: 36px 40px;
          margin-bottom: 24px;
          box-shadow: 0 2px 12px rgba(0,0,0,0.06);
          box-sizing: border-box;
        }

        /* PRINT STYLESHEET MATCHING THE OFFICIAL PDF */
        @media print {
          body * {
            visibility: hidden;
          }
          .no-print, .no-print-layout > aside, header {
            display: none !important;
          }
          .sx-template-root, .sx-template-root * {
            visibility: visible;
          }
          .sx-template-root {
            position: absolute;
            left: 0;
            top: 0;
            width: 100% !important;
            transform: none !important;
            background: white !important;
            padding: 0 !important;
            margin: 0 !important;
          }
          .sx-page {
            width: 100% !important;
            min-height: auto !important;
            padding: 10mm 12mm !important;
            margin: 0 !important;
            box-shadow: none !important;
            page-break-after: always !important;
            break-after: page !important;
          }
          @page {
            size: A4 portrait;
            margin: 10mm 12mm;
          }
        }
      `}</style>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   HELPER: FORMAT TABLES INTO ACADEMIC STYLE
   ───────────────────────────────────────────────────────────────────────── */
function FormattedTable({ raw }) {
  const lines = raw.split(/\r?\n/).filter(Boolean);
  const title = lines.find((l) => l.startsWith("Table ")) || "";
  const source = lines.find((l) => l.toLowerCase().startsWith("source:")) || "";
  const tableLines = lines.filter((l) => l.includes("|") && !l.startsWith("Table "));

  const rows = tableLines.map((line) =>
    line.split("|").map((cell) => cell.trim()).filter((_, idx, arr) => idx > 0 && idx < arr.length - 1)
  );

  const headerRow = rows[0] || [];
  const bodyRows = rows.slice(1);

  return (
    <div style={{ margin: "16px 0", breakInside: "avoid-column" }}>
      {title && (
        <div style={{ fontFamily: "Georgia, serif", fontSize: "10.5px", fontStyle: "italic", color: "#991B1B", marginBottom: "6px", fontWeight: 600 }}>
          {title}
        </div>
      )}
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "9.5px", fontFamily: "Arial, sans-serif" }}>
        {headerRow.length > 0 && (
          <thead>
            <tr style={{ borderTop: "1.5px solid #111827", borderBottom: "1px solid #111827" }}>
              {headerRow.map((h, i) => (
                <th key={i} style={{ padding: "5px 6px", textAlign: "left", fontWeight: 700, color: "#111827" }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {bodyRows.map((r, rIdx) => (
            <tr key={rIdx} style={{ borderBottom: rIdx === bodyRows.length - 1 ? "1.5px solid #111827" : "1px solid #E5E7EB" }}>
              {r.map((cell, cIdx) => (
                <td key={cIdx} style={{ padding: "4px 6px", color: "#374151" }}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {source && (
        <div style={{ fontFamily: "Arial, sans-serif", fontSize: "8.5px", fontStyle: "italic", color: "#6B7280", marginTop: "4px" }}>
          {source}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   HELPER: FORMAT FIGURE CALLOUT BOXES
   ───────────────────────────────────────────────────────────────────────── */
function FormattedFigure({ raw }) {
  const lines = raw.split(/\r?\n/).filter(Boolean);
  const caption = lines[0] || "";
  const content = lines.slice(1).join(" ");

  return (
    <div
      style={{
        margin: "18px 0",
        border: "1px solid #E5E7EB",
        background: "#F9FAFB",
        padding: "12px",
        breakInside: "avoid-column",
        textAlign: "center",
      }}
    >
      {content && (
        <div style={{ fontFamily: "Arial, sans-serif", fontSize: "9.5px", color: "#4B5563", marginBottom: "8px", fontStyle: "italic" }}>
          {content}
        </div>
      )}
      <div style={{ fontFamily: "Arial, sans-serif", fontSize: "10px", fontWeight: 700, color: "#111827" }}>
        {caption}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   HELPER: RENDER CITATION REFERENCE WITH CLICKABLE LINKS
   ───────────────────────────────────────────────────────────────────────── */
function formatReferenceText(refText) {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = refText.split(urlRegex);
  return parts.map((part, idx) => {
    if (part.match(urlRegex)) {
      return (
        <a key={idx} href={part} target="_blank" rel="noreferrer" className="sx-ref-link">
          {part}
        </a>
      );
    }
    return part;
  });
}

const printStyleSheet = `
  .sx-two-columns { column-count: 2; column-gap: 28px; text-align: justify; }
  .sx-heading-1 { font-family: Arial, sans-serif; font-size: 13px; font-weight: bold; color: #991B1B; margin: 16px 0 8px; }
  .sx-heading-2 { font-family: Arial, sans-serif; font-size: 11.5px; font-weight: bold; color: #991B1B; margin: 12px 0 6px; }
  .sx-body-p { font-family: Arial, sans-serif; font-size: 10.5px; line-height: 1.55; color: #1F2937; text-align: justify; margin-bottom: 10px; }
  .sx-reference-item { font-family: Arial, sans-serif; font-size: 9.5px; line-height: 1.45; padding-left: 20px; text-indent: -20px; }
  .sx-side-label { font-family: Arial, sans-serif; font-size: 9px; font-weight: bold; color: #111827; }
  .sx-side-val { font-family: Arial, sans-serif; font-size: 10px; color: #374151; }
`;
